/**
 * Master Tour Calendar (v2) design-bar prefs.
 * Mirrors the Claude prototype controls: density, weekStart, timeFormat,
 * travel/flight/perdiem toggles, accent. Persisted locally — not a live GDS feed.
 */

export const V2_PREFS_KEY = 'cal_v2_prefs';
export const V2_PREFS_EVENT = 'cal-v2-prefs';

export const V2_ACCENTS = ['#c08133', '#5a6fa8', '#4a6396', '#2a7d5c'];

export const DEFAULT_LANE_COLORS = {
  show: '#3dff9a',
  travel: '#6eb6ff',
  off: '#e0b15a',
  opener: '#c08133',
  personnel: '#9b7dff',
};

export const DEFAULT_CHAT_COLORS = {
  accent: '#3dff9a',
  bubble: '#2a2a33',
  mine: '#3dff9a',
  channel: '#8fd4b8',
};

export const DEFAULT_V2_PREFS = {
  density: 'comfortable',
  weekStart: 'sunday',
  timeFormat: '12-hour',
  showTravelDetail: true,
  liveFlightStatus: false,
  showPerDiems: false,
  accent: '#2a7d5c',
  accents: [...V2_ACCENTS],
  accentSlot: 3,
  riderActColors: {},
  laneColors: { ...DEFAULT_LANE_COLORS },
  chatColors: { ...DEFAULT_CHAT_COLORS },
};

export function hexWithAlpha(hex, alpha, fallback = V2_ACCENTS[3]) {
  const normalized = normalizeAccentHex(hex, fallback);
  const a = Math.round(Math.min(1, Math.max(0, Number(alpha) || 0)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${normalized}${a}`;
}

function byteToHex(n) {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}

export function parseHexRgb(hex, fallback = V2_ACCENTS[3]) {
  const n = normalizeAccentHex(hex, fallback).slice(1);
  return {
    r: parseInt(n.slice(0, 2), 16),
    g: parseInt(n.slice(2, 4), 16),
    b: parseInt(n.slice(4, 6), 16),
  };
}

/** Mix two hex colors. `t = 0` is `a`, `t = 1` is `b`. */
export function mixHex(a, b, t, fallback = V2_ACCENTS[3]) {
  const A = parseHexRgb(a, fallback);
  const B = parseHexRgb(b, fallback);
  const k = Math.max(0, Math.min(1, Number(t) || 0));
  return `#${byteToHex(A.r + (B.r - A.r) * k)}${byteToHex(A.g + (B.g - A.g) * k)}${byteToHex(A.b + (B.b - A.b) * k)}`;
}

export function hexLuminance(hex, fallback = V2_ACCENTS[3]) {
  const { r, g, b } = parseHexRgb(hex, fallback);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Glossy wash for settlement totals and day-strip chips.
 * Light: pale sheen of the color + dark type. Dark: lifted sheen + light type.
 */
export function glossyFill(hex, { isDarkMode = false, selected = false, role = 'chip' } = {}) {
  const base = normalizeAccentHex(hex);
  const panel = role === 'panel';
  const punch = selected ? 0.08 : 0;
  let top;
  let mid;
  let deep;
  if (isDarkMode) {
    top = mixHex(base, '#ffffff', panel ? 0.2 : 0.28 + punch);
    mid = mixHex(base, '#14141a', panel ? 0.42 : 0.22);
    deep = mixHex(base, '#050508', panel ? 0.58 : 0.38);
  } else if (panel) {
    top = mixHex(base, '#ffffff', 0.82);
    mid = mixHex(base, '#ffffff', 0.58);
    deep = mixHex(base, '#ffffff', 0.32);
  } else {
    top = mixHex(base, '#ffffff', 0.48 - punch);
    mid = mixHex(base, '#ffffff', 0.22 - punch);
    deep = mixHex(base, '#000000', 0.1 + punch);
  }
  const lum = hexLuminance(mid);
  const lightType = lum < 0.55;
  const ink = lightType ? '#f4f4f2' : '#14141a';
  const mute = lightType ? 'rgba(244,244,242,0.72)' : '#5c5c56';
  const sheen = lightType ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.88)';
  const under = lightType ? 'rgba(0,0,0,0.32)' : 'rgba(0,0,0,0.1)';
  return {
    base,
    top,
    mid,
    deep,
    ink,
    mute,
    lightType,
    backgroundColor: mid,
    backgroundImage: `linear-gradient(165deg, ${top} 0%, ${mid} 46%, ${deep} 100%)`,
    boxShadow: `inset 0 1px 0 ${sheen}, inset 0 -1px 0 ${under}`,
    borderColor: mixHex(base, lightType ? '#ffffff' : '#000000', 0.22),
    well: lightType ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.55)',
    line: lightType ? 'rgba(255,255,255,0.14)' : 'rgba(20,20,26,0.12)',
  };
}

export function glossyWebStyle(fill) {
  return {
    backgroundColor: fill.backgroundColor,
    borderColor: fill.borderColor,
    backgroundImage: fill.backgroundImage,
    boxShadow: fill.boxShadow,
  };
}

export function normalizeAccentHex(value, fallback = V2_ACCENTS[3]) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

export function normalizeAccentSwatches(list) {
  const incoming = Array.isArray(list) ? list : [];
  return V2_ACCENTS.map((fallback, i) => normalizeAccentHex(incoming[i], fallback));
}

function clampSlot(n) {
  const i = Number(n);
  if (!Number.isFinite(i)) return 3;
  return Math.max(0, Math.min(3, Math.floor(i)));
}

export function selectAccentSwatch(prefs, index) {
  const accents = normalizeAccentSwatches(prefs?.accents);
  const accentSlot = clampSlot(index);
  return { accents, accentSlot, accent: accents[accentSlot] };
}

/** Recolor the currently selected default swatch (one at a time) and use it as the accent. */
export function applyPickerColor(prefs, hex) {
  const accents = normalizeAccentSwatches(prefs?.accents);
  const accentSlot = clampSlot(prefs?.accentSlot);
  const accent = normalizeAccentHex(hex, accents[accentSlot]);
  const next = accents.slice();
  next[accentSlot] = accent;
  return { accents: next, accentSlot, accent };
}

export function normalizeLaneColors(raw, fallbackShow = DEFAULT_LANE_COLORS.show) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    show: normalizeAccentHex(src.show, fallbackShow || DEFAULT_LANE_COLORS.show),
    travel: normalizeAccentHex(src.travel, DEFAULT_LANE_COLORS.travel),
    off: normalizeAccentHex(src.off, DEFAULT_LANE_COLORS.off),
    opener: normalizeAccentHex(src.opener, DEFAULT_LANE_COLORS.opener),
    personnel: normalizeAccentHex(src.personnel, DEFAULT_LANE_COLORS.personnel),
  };
}

export function normalizeChatColors(raw) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    accent: normalizeAccentHex(src.accent, DEFAULT_CHAT_COLORS.accent),
    bubble: normalizeAccentHex(src.bubble, DEFAULT_CHAT_COLORS.bubble),
    mine: normalizeAccentHex(src.mine, DEFAULT_CHAT_COLORS.mine),
    channel: normalizeAccentHex(src.channel, DEFAULT_CHAT_COLORS.channel),
  };
}

export function resolveChatColors(prefs, pal) {
  const c = normalizeChatColors(prefs?.chatColors);
  const accent = c.accent || prefs?.accent || pal?.teal || DEFAULT_CHAT_COLORS.accent;
  return {
    accent,
    bubble: c.bubble || pal?.surf || DEFAULT_CHAT_COLORS.bubble,
    mine: c.mine || accent,
    channel: c.channel || pal?.inkSoft || DEFAULT_CHAT_COLORS.channel,
  };
}

function normalizeRiderActColors(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  Object.keys(raw).forEach((tourId) => {
    const map = raw[tourId];
    if (!tourId || !map || typeof map !== 'object' || Array.isArray(map)) return;
    const inner = {};
    Object.keys(map).forEach((actId) => {
      if (!actId || actId === 'combined') return;
      inner[actId] = normalizeAccentHex(map[actId], V2_ACCENTS[0]);
    });
    if (Object.keys(inner).length) out[tourId] = inner;
  });
  return out;
}

function normalizePrefs(raw) {
  const merged = { ...DEFAULT_V2_PREFS, ...(raw && typeof raw === 'object' ? raw : {}) };
  const accents = normalizeAccentSwatches(merged.accents);
  let accentSlot = clampSlot(merged.accentSlot);
  let accent = normalizeAccentHex(merged.accent, accents[accentSlot]);
  const match = accents.indexOf(accent);
  if (match >= 0) accentSlot = match;
  else accents[accentSlot] = accent;
  return {
    ...merged,
    accents,
    accentSlot,
    accent,
    riderActColors: normalizeRiderActColors(merged.riderActColors),
    laneColors: normalizeLaneColors(merged.laneColors, accent),
    chatColors: normalizeChatColors(merged.chatColors),
  };
}

export function loadV2Prefs() {
  try {
    if (typeof localStorage === 'undefined') return normalizePrefs(DEFAULT_V2_PREFS);
    const raw = localStorage.getItem(V2_PREFS_KEY);
    if (!raw) return normalizePrefs(DEFAULT_V2_PREFS);
    const parsed = JSON.parse(raw);
    return normalizePrefs(parsed);
  } catch {
    return normalizePrefs(DEFAULT_V2_PREFS);
  }
}

export function saveV2Prefs(prefs) {
  const next = normalizePrefs(prefs);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(V2_PREFS_KEY, JSON.stringify(next));
    }
  } catch {
    /* ignore quota */
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(V2_PREFS_EVENT));
  }
  return next;
}

export function resetV2Prefs() {
  return saveV2Prefs({ ...DEFAULT_V2_PREFS, accents: [...V2_ACCENTS] });
}

export function weekStartsOnFromPrefs(prefs) {
  return prefs?.weekStart === 'monday' ? 1 : 0;
}
